import "vite/modulepreload-polyfill";
import {
  ShopSystemStrategyFactory,
  type IShopSystemStrategy,
  type SitePayload,
} from "./lib/ShopSystemStrategy/IShopSystemStrategy";

import GlobalStyles from "./GlobalStyles.svelte";
import setupPostHog from "./lib/posthog";
import setupSentry from "./lib/sentry";
import { App } from "./app";

type EnrichedWindow = typeof window & { pinaLoaded?: boolean };
type EnrichedDocument = typeof document & {
  currentScript?: HTMLOrSVGScriptElement & { src?: string };
};

// @ts-ignore
const payload = window.payload as SitePayload;
const shopSystemStrategy: IShopSystemStrategy = ShopSystemStrategyFactory(
  payload.shopSystem,
);

const w = window as EnrichedWindow;
const d = document as EnrichedDocument;

if (
  isPluginEnabled() &&
  // Check if this is a production app running live in userscript dev mode.
  !isLiveDevMode() &&
  !w.pinaLoaded
) {
  w.pinaLoaded = true;

  console.log("Loaded", d.currentScript?.src);

  if (window.location.pathname.startsWith("/build/boxspringbett")) {
    payload.page = "mozartKonfigurator";
  }

  setupSentry();
  setupPostHog(window.location.hostname);

  const timeout = setInterval(() => {
    // Create element to load global styles.
    const target = document.createElement("div");
    target.id = "pina-app-main";
    if (document.body) {
      document.body.appendChild(target);

      // Add global styles as svelte component.
      new GlobalStyles({
        target: target as HTMLElement,
        props: {
          page: payload.page,
          shopSystemStrategy: shopSystemStrategy,
        },
      });

      // Load app.
      new App(shopSystemStrategy).load(payload.page);

      clearInterval(timeout);
    }
  }, 100);
}

function isPluginEnabled(): boolean {
  const params = new URLSearchParams(window.location.search);
  const enableIntegrationParam = params.get("pina");
  if (enableIntegrationParam !== null) {
    window.localStorage.setItem("pina", enableIntegrationParam);
  }

  const liveUrls = ["mozart-bett", "micaraa", "this.place", "rookie-baby"];
  const isLiveUrl = liveUrls.some((x) => document.URL.includes(x));
  const isDev = process.env.NODE_ENV === "development";

  return isLiveUrl || isDev || window.localStorage.getItem("pina") === "true";
}

function isLiveDevMode(): boolean {
  const productionPrefix = "https://static.climate-app.pina.earth";

  //@ts-ignore
  // This only works in the built version of the app.
  const src = document.currentScript?.src;

  if (!src) {
    return false;
  }

  // Check if the currently running version was loaded from a userscript.
  const version = new URL(src).searchParams.get("version");
  if (version == "userscript") {
    return false;
  }

  const isProduction = src.startsWith(productionPrefix);

  const devMode =
    window.sessionStorage.getItem("dev") === "true" && isProduction;

  // Remove original elements.
  // This is mainly important to remove CSS rule clashing.
  if (devMode) {
    document
      .querySelector(`script[src='${productionPrefix}/main.js']`)
      ?.remove();
    document
      .querySelector(`link[src='${productionPrefix}/main.css']`)
      ?.remove();
  }

  return devMode;
}
